import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Chip, DChip, DataTable, EmptyState, Icon, Modal, Pane, PaneHead, PhoneCards, PhoneScreen, PickerChip,
} from '@/components/kit';
import type { TableRow } from '@/components/kit';
import { useShellChrome } from '@/layouts/shellChrome';
import { useShellIdentity } from '@/components/shell/useShellIdentity';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useAuthStore } from '@/stores/useAuthStore';
import { Permission } from '@/types/user';
import { StockItemPane } from './StockItemPane';
import {
  expiryLabel,
  expiryTone,
  useSetMin,
  useOrders,
  useShelf,
  useTransfers,
  useWriteOff,
  type Order,
  type ShelfItem,
  type ShelfLot,
  type Transfer,
} from './lib/useStock';
import { StockMenu } from './StockMenu';

/**
 * Stock · The shelf — `g_shelf` (f1). FILTER-HEAVY, THE ITEM DOCKED.
 *
 * THE SHELF OPENS FILTERED, which is the design's strongest claim about this
 * screen: a clinic with 118 items does not want 118 rows, it wants the five
 * that need a decision today. So `Low` and `Expiring 30 d` are ON when the
 * screen opens, joined by OR, and the line above the table says so in words —
 * "Showing: low OR expiring within 30 days … all 5 matches of 118 items" —
 * because a filtered list that does not admit it is filtered is how somebody
 * concludes the clinic only stocks five things.
 *
 * SORTED BY EXPIRY, SERVER-SIDE. `GET /stock` already ranks what needs a
 * decision first and orders each item's lots earliest-first, so the shelf and
 * the pane cannot disagree about which box to open. The chips here narrow what
 * has already arrived — "low OR expiring" over a clinic's own shelf is a
 * handful of rows, and a round-trip per chip would make this feel slower than
 * walking to the cupboard.
 *
 * WHAT IS NOT DRAWN HERE. The board's IN FLIGHT strip (draft · ordered ·
 * confirmed · in transit) reads from purchase orders and transfers — the f5 and
 * f7 boards' own model. A clinic with no open order has nothing in flight and
 * should see no strip, which is also the strip's correct resting state once
 * those land, so it renders from the count and is absent at zero.
 */

/* One stable empty array — `shelf ?? []` is a new array each render, which
   silently defeats every useMemo below it. */
const NONE: ShelfItem[] = [];

type SortKey = 'expiry' | 'shortfall' | 'name';

const SORTS = [
  { value: 'expiry' as const, label: 'Sort: expiry' },
  { value: 'shortfall' as const, label: 'Sort: furthest under min', hint: 'proportionally, not by count' },
  { value: 'name' as const, label: 'Sort: name' },
];

/** The caption under an item's name: what it is measured in, and how it is kept. */
function itemSub(item: ShelfItem): string {
  return `${item.unit || item.category || 'item'} · ${item.batchTracked ? 'batch-tracked' : 'no batches'}`;
}

export function StockShelfPage() {
  const go = useNavigate();
  const { branch } = useShellIdentity();
  const branchName = branch.replace(/\s*▾\s*$/, '');
  const roleCheck = useAuthStore((s) => s.roleCheck);
  const canWrite = roleCheck.hasPermission(Permission.STOCK_WRITE);

  const { data: shelf, isLoading, isError } = useShelf();
  const items = shelf ?? NONE;
  const { data: orders } = useOrders(false);
  const { data: transfers } = useTransfers(null);

  const [query, setQuery] = useState('');
  const [low, setLow] = useState(true);
  const [expiring, setExpiring] = useState(true);
  const [batchOnly, setBatchOnly] = useState(false);
  const [sellsOnly, setSellsOnly] = useState(false);
  const [category, setCategory] = useState('');
  const [sort, setSort] = useState<SortKey>('expiry');
  const [pickedId, setPicked] = useState<string | null>(null);
  const [editMin, setEditMin] = useState<ShelfItem | null>(null);
  const [writeOff, setWriteOff] = useState<{ item: ShelfItem; lot: ShelfLot; ret?: boolean } | null>(null);

  usePageMeta({ title: 'Stock' });

  const categories = useMemo(
    () => [...new Set(items.map((i) => i.category).filter((c): c is string => !!c))].sort(),
    [items],
  );

  const counts = useMemo(
    () => ({
      items: items.length,
      expiring: items.filter((i) => i.expiringSoon).length,
      low: items.filter((i) => i.low).length,
    }),
    [items],
  );

  /* THE TWO NEED-CHIPS ARE OR, NOT AND — "low OR expiring" is one question
     ("what needs me?"); batch-tracked, billable and category narrow whatever
     that answered. Search matches the barcode too, because the board's search
     says "Name or barcode" and a stock manager holding a box reads the code. */
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const shortfall = (i: ShelfItem) =>
      i.minLevel && i.minLevel > 0 ? i.inStock / i.minLevel : Number.POSITIVE_INFINITY;
    return items
      .filter((i) => {
        if ((low || expiring) && !((low && i.low) || (expiring && i.expiringSoon))) return false;
        if (batchOnly && !i.batchTracked) return false;
        if (sellsOnly && !i.sellable) return false;
        if (category && i.category !== category) return false;
        if (q && !`${i.name} ${i.barcode ?? ''}`.toLowerCase().includes(q)) return false;
        return true;
      })
      .sort((a, b) => {
        if (sort === 'name') return a.name.localeCompare(b.name);
        if (sort === 'shortfall') return shortfall(a) - shortfall(b) || a.name.localeCompare(b.name);
        return (
          (a.nextExpiry ?? '9999-12-31').localeCompare(b.nextExpiry ?? '9999-12-31') ||
          a.name.localeCompare(b.name)
        );
      });
  }, [items, query, low, expiring, batchOnly, sellsOnly, category, sort]);

  const picked = items.find((i) => i.id === pickedId) ?? null;
  const narrowed = low || expiring || batchOnly || sellsOnly || !!category || !!query.trim();

  const setMin = useSetMin();
  const adjust = useWriteOff();


  const tableRows: TableRow[] = rows.map((item) => {
    const tone = expiryTone(item.nextExpiry);
    return {
      id: item.id,
      cells: {
        item: (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <span className="stock-thumb" aria-hidden="true">
              {item.photoUrl ? <img src={item.photoUrl} alt="" /> : <Icon name="photo" />}
            </span>
            <div style={{ minWidth: 0 }}>
              <p className="t-strong">
                {item.name}
                {item.sellable ? (
                  <>
                    {' '}
                    <Chip kind="brand" small>Sells</Chip>
                  </>
                ) : null}
              </p>
              <p className="t-cap mut">{itemSub(item)}</p>
            </div>
          </div>
        ),
        qty: <span className={`t-strong${item.low ? ' amb' : ''}`}>{item.inStock}</span>,
        min: <span className="mut">{item.minLevel ?? '—'}</span>,
        exp: tone ? (
          <Chip kind={tone} small>{expiryLabel(item.nextExpiry)}</Chip>
        ) : (
          <span className="mut">{expiryLabel(item.nextExpiry)}</span>
        ),
        act: canWrite ? <DChip onClick={() => setEditMin(item)}>Min</DChip> : null,
      },
    };
  });

  /* The same chips on the desk and the phone; the phone drops the two that
     narrow by a property (`g_k1` draws Low, Expiring, Category and Sort). */
  const filterChips = (full: boolean) => (
    <>
      <Chip kind={low ? 'brand' : 'outline'} small onClick={() => setLow((v) => !v)}>
        {low ? 'Low ✓' : 'Low'}
      </Chip>
      <Chip kind={expiring ? 'brand' : 'outline'} small onClick={() => setExpiring((v) => !v)}>
        {expiring ? (full ? 'Expiring 30 d ✓' : 'Expiring ✓') : full ? 'Expiring 30 d' : 'Expiring'}
      </Chip>
      {categories.length ? (
        <PickerChip
          label={`${category || 'Category'} ▾`}
          ariaLabel="Category"
          kind={category ? 'brand' : 'outline'}
          value={category}
          onChange={setCategory}
          options={[{ value: '', label: 'Every category' }, ...categories.map((c) => ({ value: c, label: c }))]}
        />
      ) : null}
      {full ? (
        <>
          <Chip kind={batchOnly ? 'brand' : 'outline'} small onClick={() => setBatchOnly((v) => !v)}>
            {batchOnly ? 'Batch-tracked ✓' : 'Batch-tracked'}
          </Chip>
          <Chip kind={sellsOnly ? 'brand' : 'outline'} small onClick={() => setSellsOnly((v) => !v)}>
            {sellsOnly ? 'Billable products ✓' : 'Billable products'}
          </Chip>
        </>
      ) : null}
      <PickerChip
        label={`${SORTS.find((s) => s.value === sort)?.label ?? 'Sort'} ▾`}
        ariaLabel="Sort"
        value={sort}
        onChange={setSort}
        options={SORTS}
      />
    </>
  );

  /* `g_k1`: a card is the item and ONE line of figures, with Edit on the right.
     "3 left · min 8" is the shortfall in words; a dated box says when. */
  const phoneRows: TableRow[] = rows.map((item) => {
    const tone = expiryTone(item.nextExpiry);
    const bits: ReactNode[] = [`${item.inStock}${item.unit ? ` ${item.unit}` : ''}`];
    if (item.low) bits[0] = <span key="q" className="amb">{item.inStock} left · min {item.minLevel}</span>;
    if (item.nextExpiry) {
      bits.push(
        <span key="e" className={tone === 'danger' ? 'red' : tone === 'warn' ? 'amb' : undefined}>
          exp {expiryLabel(item.nextExpiry)}
        </span>,
      );
    }
    const inDraft = (orders ?? []).some(
      (o) => o.status === 'draft' && o.lines.some((l) => l.itemId === item.id),
    );
    if (inDraft) bits.push(<span key="d" className="grn">in draft order ✓</span>);
    return {
      id: item.id,
      cells: {
        item: (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <span className="stock-thumb" aria-hidden="true">
              {item.photoUrl ? <img src={item.photoUrl} alt="" /> : <Icon name="photo" />}
            </span>
            <div style={{ minWidth: 0 }}>
              <p className="t-strong">{item.name}</p>
              <p className="t-cap mut">
                {bits.map((b, i) => (
                  <span key={i}>{i ? ' · ' : ''}{b}</span>
                ))}
              </p>
            </div>
          </div>
        ),
        act: canWrite ? (
          <DChip onClick={() => go(`/stock-manager/catalog?edit=${item.id}`)}>Edit</DChip>
        ) : null,
      },
    };
  });

  const openOrders = (orders ?? []).filter(
    (o) => o.status === 'draft' || o.status === 'ordered' || o.status === 'confirmed',
  ).length;

  useShellChrome(
    {
      raw: true,
      /* Phone: the pick is its own screen; back clears it. */
      paneOpen: !!picked,
      onPaneClose: () => setPicked(null),
      paneMenu: (close) => <StockMenu door="shelf" onClose={close} />,
      /* `g_k6`: the picked item's screen reads "Stock ▸ <item>". */
      phoneTitle: 'Stock',
      paneTitle: picked?.name,
      /* `g_k1` — the desk table as cards. The rows are the table's
         own, so a figure or a state can never read differently here. */
      phone: (
        <PhoneScreen
          className="stock-phone"
          title={`Stock · ${branchName}`}
          menu={(close) => <StockMenu door="shelf" onClose={close} />}
          search={{ placeholder: 'Name or scan a barcode', value: query, onChange: setQuery }}
          trailing={
            <button type="button" className="ts-btn" aria-label="Item catalog"
              onClick={() => go('/stock-manager/catalog')}>
              <Icon name="list-details" size="lg" />
            </button>
          }
        >
          {/* `g_k1` — the shelf's own verbs sit above it on a phone, where
              there is no header to hold them. */}
          {canWrite ? (
            <div className="filterrow stock-phone-verbs">
              <DChip kind="brand" onClick={() => go('/stock-manager/receive')}>+ Receive</DChip>
              <DChip onClick={() => go('/stock-manager/count')}>Count</DChip>
              <DChip onClick={() => go('/stock-manager/transfers')}>⇄ Transfer</DChip>
              <DChip onClick={() => go('/stock-manager/orders')}>
                {openOrders ? `Orders · ${openOrders}` : 'Orders'}
              </DChip>
            </div>
          ) : null}
          <div className="filterrow stock-phone-verbs">{filterChips(false)}</div>
          <p className="t-cap mut mb8">
            {describe({ low, expiring, batchOnly, sellsOnly, category, query, sort, shown: rows.length, total: counts.items })}
          </p>
          <PhoneCards
            columns={[
              { key: 'item', label: 'Item', width: 80 },
              { key: 'act', label: '', width: 20 },
            ]}
            rows={phoneRows}
            body="item"
            trailing="act"
            onOpen={(r) => setPicked(r.id)}
            cardStyle={(r) => {
              const it = items.find((i) => i.id === r.id);
              const t = it ? expiryTone(it.nextExpiry) : null;
              return t === 'danger'
                ? { borderColor: 'var(--danger-600)' }
                : it?.low || t === 'warn'
                  ? { borderColor: 'var(--warning-600)' }
                  : undefined;
            }}
            empty={<EmptyState title={counts.items ? 'Nothing needs you on this shelf' : 'The shelf is empty'}
              sub={counts.items ? 'No item matches these filters.' : 'Nothing has been received yet — Receive puts the first box here.'} />}
          />
        </PhoneScreen>
      ),
      title: `Stock · ${branchName}`,
      sub: isLoading
        ? 'reading the shelf…'
        : `${counts.items} item${counts.items === 1 ? '' : 's'} · ${counts.expiring} expiring · ${counts.low} low`,
      search: 'Name or barcode',
      searchValue: query,
      onSearchChange: setQuery,
      ...(canWrite
        ? { primary: { label: '+ Receive', onClick: () => go('/stock-manager/receive') } }
        : {}),
      pane: picked ? (
        <StockItemPane
          item={picked}
          branchName={branchName}
          canWrite={canWrite}
          onEditMin={() => setEditMin(picked)}
          onEdit={() => go(`/stock-manager/catalog?edit=${picked.id}`)}
          onTransfer={() => go(`/stock-manager/transfers?add=${picked.id}`)}
          onWriteOff={(lot, ret) => setWriteOff({ item: picked, lot, ret })}
          onClose={() => setPicked(null)}
        />
      ) : (
        <Pane
          head={
            <PaneHead
              overline={`Item · ${branchName}`}
              title="Nothing picked"
              sub="pick a row — its batches, its rate and every movement dock here"
            />
          }
        >
          <div className="emptystate">
            <p className="t-sec">
              The shelf answers three questions about an item: how much is there, which box to open
              first, and where the rest of it went.
            </p>
          </div>
        </Pane>
      ),
    },
    [branchName, picked, isLoading, counts.items, counts.expiring, counts.low, query, canWrite, pickedId, rows, orders, low, expiring, category, sort, batchOnly, sellsOnly, categories],
  );

  return (
    <div className="maie desk stock-root">
      <div className="filterrow">{filterChips(true)}</div>

      <div className="dcontent scroll">
        <InFlight orders={orders ?? []} transfers={transfers ?? []} branchName={branchName} go={go} />
        {canWrite ? (
          <div className="flex mb8" style={{ gap: 8, justifyContent: 'flex-end' }}>
            <DChip onClick={() => go('/stock-manager/count')}>Count</DChip>
            <DChip onClick={() => go('/stock-manager/transfers')}>⇄ Transfer</DChip>
          </div>
        ) : null}
        <p className="t-sec mut mb8">
          {isError
            ? 'The shelf did not come back. Nothing has been changed.'
            : describe({ low, expiring, batchOnly, sellsOnly, category, query, sort, shown: rows.length, total: counts.items })}
        </p>

        <DataTable
          caption={`The ${branchName} shelf`}
          columns={[
            { key: 'item', label: 'Item', width: 46 },
            { key: 'qty', label: 'Qty', width: 12 },
            { key: 'min', label: 'Min', width: 11 },
            { key: 'exp', label: 'Earliest exp.', width: 18 },
            { key: 'act', label: '', width: 13 },
          ]}
          rows={tableRows}
          selectedId={pickedId}
          onSelect={(r) => setPicked(r.id === pickedId ? null : r.id)}
          empty={
            <div className="emptystate">
              {isLoading ? (
                <p className="t-sec mut">Reading the shelf…</p>
              ) : counts.items === 0 ? (
                <>
                  <p className="es-t">Nothing is on the shelf yet</p>
                  <p className="t-sec mut">
                    The shelf fills as the clinic receives what it buys — every number here comes
                    from a receipt, a use or a count, never from typing over it.
                  </p>
                  {canWrite ? (
                    <p className="mt12">
                      <DChip kind="brand" onClick={() => go('/stock-manager/receive')}>
                        Receive the first delivery
                      </DChip>
                    </p>
                  ) : null}
                </>
              ) : narrowed ? (
                <>
                  <p className="es-t">Nothing needs you on this shelf</p>
                  <p className="t-sec mut">
                    {low && expiring && !batchOnly && !sellsOnly && !category && !query.trim()
                      ? counts.items === 1
                        ? 'The one item on the shelf is above its minimum, and nothing expires within thirty days.'
                        : `All ${counts.items} items are above their minimum, and nothing expires within thirty days.`
                      : 'No item matches these filters.'}
                  </p>
                  <p className="mt12">
                    <DChip
                      onClick={() => {
                        setLow(false);
                        setExpiring(false);
                        setBatchOnly(false);
                        setSellsOnly(false);
                        setCategory('');
                        setQuery('');
                      }}
                    >
                      Show the whole shelf
                    </DChip>
                  </p>
                </>
              ) : null}
            </div>
          }
        />
      </div>

      <MinModal
        item={editMin}
        onClose={() => setEditMin(null)}
        onSave={(minLevel) => {
          if (editMin) setMin.mutate({ id: editMin.id, name: editMin.name, minLevel });
          setEditMin(null);
        }}
      />
      <WriteOffModal
        target={writeOff}
        onClose={() => setWriteOff(null)}
        onSave={(quantity, note) => {
          if (writeOff) {
            adjust.mutate({
              itemId: writeOff.item.id,
              lotId: writeOff.lot.id,
              quantity,
              note,
              reason: writeOff.ret ? 'return' : 'waste',
            });
          }
          setWriteOff(null);
        }}
      />
    </div>
  );
}


/**
 * `g_shelf` — IN FLIGHT: what has been bought or moved and is not on the shelf
 * yet. It reads purchase orders and transfers, so a clinic with nothing open
 * sees no strip at all; a card appears only for a state that has something in it.
 */
function InFlight({
  orders,
  transfers,
  branchName,
  go,
}: {
  orders: Order[];
  transfers: Transfer[];
  branchName: string;
  go: (to: string) => void;
}) {
  const of = (status: Order['status']) => orders.filter((o) => o.status === status);
  const draft = of('draft');
  const ordered = of('ordered');
  const confirmed = of('confirmed');
  const moving = transfers.filter((t) => t.status === 'in_transit');
  if (!draft.length && !ordered.length && !confirmed.length && !moving.length) return null;

  const lines = (o: Order) => `${o.lines.length} line${o.lines.length === 1 ? '' : 's'}`;
  const cards: Array<{ key: string; label: string; head: string; sub: string; to: string }> = [];
  if (draft.length) {
    const o = draft[0];
    cards.push({
      key: 'draft', label: 'Draft', to: '/stock-manager/orders',
      head: `${o.supplierName ?? 'A supplier'} · ${lines(o)}${draft.length > 1 ? ` · +${draft.length - 1} more` : ''}`,
      sub: o.lines.slice(0, 2).map((l) => `${l.itemName} ×${l.quantity}`).join(' · '),
    });
  }
  if (ordered.length) {
    const o = ordered[0];
    cards.push({
      key: 'ordered', label: 'Ordered', to: '/stock-manager/orders',
      head: `${o.number ?? 'Order'} · ${o.supplierName ?? 'supplier'}${ordered.length > 1 ? ` · +${ordered.length - 1} more` : ''}`,
      sub: `${lines(o)}${o.ageingDays != null ? ` · ${o.ageingDays} d, no answer` : ''}`,
    });
  }
  if (confirmed.length) {
    const o = confirmed[0];
    cards.push({
      key: 'confirmed', label: 'Confirmed', to: '/stock-manager/orders',
      head: `${o.number ?? 'Order'} · ${o.supplierName ?? 'supplier'}${confirmed.length > 1 ? ` · +${confirmed.length - 1} more` : ''}`,
      sub: 'Receive opens pre-filled',
    });
  }
  if (moving.length) {
    const t = moving[0];
    cards.push({
      key: 'transit', label: 'In transit', to: '/stock-manager/transfers',
      head: `${t.number ?? 'Transfer'} → ${t.direction === 'in' ? t.fromBranch ?? 'another branch' : t.toBranch ?? 'another branch'}${moving.length > 1 ? ` · +${moving.length - 1} more` : ''}`,
      sub: `${t.items} item${t.items === 1 ? '' : 's'} · awaiting confirmation`,
    });
  }
  return (
    <>
      <p className="t-ovl mut mb8">In flight — ordered, confirmed or moving. Not on the {branchName} shelf yet.</p>
      <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
        {cards.map((c) => (
          <button key={c.key} type="button" className="card text-start"
            style={{ flex: 1, margin: 0, minWidth: 0 }} onClick={() => go(c.to)}>
            <p className="t-ovl mut">{c.label}</p>
            <p className="t-strong mt4">{c.head}</p>
            {c.sub ? <p className="t-cap mut mt4">{c.sub}</p> : null}
          </button>
        ))}
      </div>
    </>
  );
}

/** The sentence above the table — what is being shown, and out of how many. */
function describe(s: {
  low: boolean;
  expiring: boolean;
  batchOnly: boolean;
  sellsOnly: boolean;
  category: string;
  query: string;
  sort: SortKey;
  shown: number;
  total: number;
}): string {
  const clauses: string[] = [];
  if (s.low && s.expiring) clauses.push('low OR expiring within 30 days');
  else if (s.low) clauses.push('low');
  else if (s.expiring) clauses.push('expiring within 30 days');
  if (s.batchOnly) clauses.push('batch-tracked');
  if (s.sellsOnly) clauses.push('billable products');
  if (s.category) clauses.push(s.category);
  if (s.query.trim()) clauses.push(`matching “${s.query.trim()}”`);
  const by =
    s.sort === 'expiry' ? 'expiry' : s.sort === 'name' ? 'name' : 'how far under the minimum';
  const head = clauses.length ? `Showing: ${clauses.join(' · ')}` : 'Showing: the whole shelf';
  const tail =
    s.shown === s.total
      ? `all ${s.total} item${s.total === 1 ? '' : 's'}`
      : `${s.shown} of ${s.total} items`;
  return `${head} · sorted by ${by} — ${tail}`;
}

/**
 * THE MINIMUM IS THE ONE NUMBER THE SHELF ITSELF OWNS. Everything else here
 * is derived from movements and cannot be typed over; the minimum is a
 * judgement about how fast the clinic uses the thing, so it is editable from
 * the row and from the pane, and the measured rate is shown beside the field
 * because that is what the judgement should be made against.
 */
function MinModal({
  item,
  onClose,
  onSave,
}: {
  item: ShelfItem | null;
  onClose: () => void;
  onSave: (minLevel: number | null) => void;
}) {
  const [value, setValue] = useState('');
  const open = !!item;
  const suggestion = item?.perMonth ? Math.ceil(item.perMonth / 2) : null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={item ? `Minimum · ${item.name}` : 'Minimum'}
      sub="below this, the shelf calls it low"
      foot={
        <>
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => onSave(value.trim() === '' ? null : Math.max(0, Number(value)))}
          >
            Save the minimum
          </button>
        </>
      }
    >
      {item ? (
        <>
          <label className="t-ovl mut" htmlFor="stock-min">Minimum</label>
          <input
            id="stock-min"
            className="fld mt4"
            type="number"
            min={0}
            inputMode="numeric"
            defaultValue={item.minLevel ?? ''}
            placeholder={item.minLevel == null ? 'no minimum set' : undefined}
            onChange={(e) => setValue(e.target.value)}
          />
          <p className="t-cap mut mt8">
            {item.perMonth
              ? `The clinic uses about ${item.perMonth} a month. ${suggestion} would be roughly a fortnight's cover.`
              : 'Nothing has been dispensed yet, so there is no measured rate to set this against.'}
          </p>
          <p className="t-cap mut mt8">Leave it empty and the shelf will never call this low.</p>
        </>
      ) : null}
    </Modal>
  );
}

/**
 * WRITING OFF IS A MOVEMENT, NOT AN EDIT — the note is required for the same
 * reason receiving does not need one: an invoice explains itself, and stock
 * leaving without a patient behind it does not. The number on the shelf never
 * changes without a line saying who changed it and why.
 */
function WriteOffModal({
  target,
  onClose,
  onSave,
}: {
  target: { item: ShelfItem; lot: ShelfLot; ret?: boolean } | null;
  onClose: () => void;
  onSave: (quantity: number, note: string) => void;
}) {
  const [qty, setQty] = useState('');
  const [note, setNote] = useState('');
  const open = !!target;
  const max = target?.lot.quantity ?? 0;
  const amount = qty.trim() === '' ? max : Math.min(max, Math.max(1, Number(qty)));
  const ready = note.trim().length > 2 && amount > 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={target?.ret ? 'Return to supplier' : 'Write off'}
      sub={target ? `${target.item.name} · ${target.lot.lotCode ?? 'this batch'}` : undefined}
      foot={
        <>
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn btn-danger"
            disabled={!ready}
            onClick={() => {
              onSave(amount, note.trim());
              setQty('');
              setNote('');
            }}
          >
            {target?.ret ? `Return ${amount}` : `Write off ${amount}`}
          </button>
        </>
      }
    >
      {target ? (
        <>
          <p className="t-sec sec">
            {max} {target.item.unit || 'units'} on this batch
            {target.lot.expiresOn ? `, dated ${expiryLabel(target.lot.expiresOn)}` : ''}.
          </p>
          <label className="t-ovl mut mt12" htmlFor="wo-qty">How many</label>
          <input
            id="wo-qty"
            className="fld mt4"
            type="number"
            min={1}
            max={max}
            inputMode="numeric"
            placeholder={`all ${max}`}
            onChange={(e) => setQty(e.target.value)}
          />
          <label className="t-ovl mut mt12" htmlFor="wo-note">Why</label>
          <input
            id="wo-note"
            className="fld mt4"
            placeholder={target?.ret ? 'damaged · wrong item · recalled' : 'expired · broke · spilled'}
            onChange={(e) => setNote(e.target.value)}
          />
          <p className="t-cap mut mt8">
            This goes on the item’s movements with your name against it. It cannot be un-said, only
            corrected by another movement.
          </p>
        </>
      ) : null}
    </Modal>
  );
}
